
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format, addDays, parse } from "date-fns";
import { SubscriptionType } from "@/lib/supabase/types";

interface EditSubscriptionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: SubscriptionType | null;
  onSave: (subscriptionId: string, updates: {
    totalClasses: number;
    remainingClasses: number;
    expirationDate: string;
  }) => void;
}

const EditSubscriptionDialog: React.FC<EditSubscriptionDialogProps> = ({
  isOpen,
  onOpenChange,
  subscription,
  onSave
}) => {
  const [totalClasses, setTotalClasses] = useState<number>(0);
  const [remainingClasses, setRemainingClasses] = useState<number>(0);
  const [expirationDate, setExpirationDate] = useState<string>("");

  // Initialize form values when subscription changes
  useEffect(() => {
    if (subscription) {
      setTotalClasses(subscription.totalClasses);
      setRemainingClasses(subscription.remainingClasses);
      setExpirationDate(format(new Date(subscription.expirationDate), 'yyyy-MM-dd'));
    }
  }, [subscription]);

  const handleSave = () => {
    if (!subscription) return;
    
    onSave(subscription.id, {
      totalClasses,
      remainingClasses,
      expirationDate: new Date(expirationDate).toISOString()
    });
  };

  // Check if the current values are different from the original ones
  const hasChanges = subscription && (
    totalClasses !== subscription.totalClasses ||
    remainingClasses !== subscription.remainingClasses ||
    expirationDate !== format(new Date(subscription.expirationDate), 'yyyy-MM-dd')
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Subscription</DialogTitle>
        </DialogHeader>
        {subscription && (
          <div className="grid gap-6 py-4">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span>Subscription for:</span>
              <span className="font-medium text-foreground">{subscription.userName || "Unknown User"}</span>
              <span>•</span>
              <span>Purchased on {format(new Date(subscription.purchaseDate), 'MMM d, yyyy')}</span>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="totalClasses">Total Classes</Label>
              <Input
                id="totalClasses"
                type="number"
                min="1"
                max="50"
                value={totalClasses}
                onChange={(e) => setTotalClasses(parseInt(e.target.value) || 0)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="remainingClasses">Remaining Classes</Label>
              <Input
                id="remainingClasses"
                type="number"
                min="0"
                max={totalClasses}
                value={remainingClasses}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setRemainingClasses(Math.min(value, totalClasses));
                }}
              />
              {remainingClasses > totalClasses && (
                <p className="text-xs text-destructive mt-1">
                  Remaining classes cannot exceed total classes.
                </p>
              )}
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="expirationDate">Expiration Date</Label>
              <Input
                id="expirationDate"
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setExpirationDate(format(addDays(new Date(), 30), 'yyyy-MM-dd'))}
                >
                  +30 days
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setExpirationDate(format(addDays(new Date(), 60), 'yyyy-MM-dd'))}
                >
                  +60 days
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setExpirationDate(format(addDays(new Date(), 90), 'yyyy-MM-dd'))}
                >
                  +90 days
                </Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!hasChanges || remainingClasses > totalClasses}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubscriptionDialog;
